import { useState } from 'react';
import { 
  FaCalculator, 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaBuilding, 
  FaLaptopCode,
  FaMobileAlt,
  FaShoppingCart,
  FaServer,
  FaPalette,
  FaArrowRight,
  FaCalendarAlt,
  FaFileAlt,
  FaStar,
  FaGift,
  FaRocket,
  FaHeart
} from 'react-icons/fa';
import styles from './Budget.module.css';

const Budget = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    budget: '',
    timeline: '',
    features: [],
    description: '',
    acceptTerms: false
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedService, setSelectedService] = useState(null);
  const [ticketNumber] = useState(`LUCQA-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`);

  const services = [
    {
      id: 'web',
      title: 'Desarrollo Web',
      icon: <FaLaptopCode />,
      description: 'Sitios web profesionales, responsive y optimizados para SEO',
      features: ['Diseño personalizado', 'Responsive', 'SEO optimizado', 'CMS integrado']
    },
    {
      id: 'app',
      title: 'Aplicaciones Móviles',
      icon: <FaMobileAlt />,
      description: 'Apps nativas e híbridas para iOS y Android',
      features: ['Diseño UX/UI', 'Desarrollo multiplataforma', 'Integración con APIs', 'Notificaciones push']
    },
    {
      id: 'ecommerce',
      title: 'E-Commerce',
      icon: <FaShoppingCart />,
      description: 'Tiendas online con gestión de productos y pagos',
      features: ['Catálogo de productos', 'Pasarela de pagos', 'Gestión de inventario', 'Panel de administración']
    },
    {
      id: 'hosting',
      title: 'Hosting y Mantenimiento',
      icon: <FaServer />,
      description: 'Alojamiento, soporte técnico y actualizaciones',
      features: ['Alojamiento seguro', 'Copias de seguridad', 'Soporte técnico', 'Actualizaciones periódicas']
    },
    {
      id: 'design',
      title: 'Diseño Gráfico',
      icon: <FaPalette />,
      description: 'Identidad visual, logotipos y material gráfico',
      features: ['Diseño de logotipo', 'Identidad corporativa', 'Material publicitario', 'Redes sociales']
    }
  ];

  const handleServiceSelect = (serviceId) => {
    setSelectedService(serviceId);
    setFormData({
      ...formData,
      projectType: serviceId
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox' && name.startsWith('feature-')) {
      const feature = name.replace('feature-', '');
      const features = [...formData.features];
      
      if (checked) {
        features.push(feature);
      } else {
        const index = features.indexOf(feature);
        if (index > -1) {
          features.splice(index, 1);
        }
      }
      
      setFormData({
        ...formData,
        features
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = 'El nombre es obligatorio';
    if (!formData.email.trim()) {
      newErrors.email = 'El email es obligatorio';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    if (!formData.phone.trim()) newErrors.phone = 'El teléfono es obligatorio';
    if (!formData.projectType) newErrors.projectType = 'Selecciona un tipo de proyecto';
    if (!formData.description.trim()) newErrors.description = 'La descripción es obligatoria';
    if (!formData.acceptTerms) newErrors.acceptTerms = 'Debe aceptar los términos';
    
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Here you would typically send the data to your backend
    console.log('Form submitted:', formData);
    
    // Show success message
    setSubmitted(true);
  };

  const getServiceTitle = () => {
    const service = services.find(s => s.id === formData.projectType);
    return service ? service.title : 'Proyecto Personalizado';
  };

  const getTimelineText = () => {
    const timelines = {
      'urgente': 'Menos de 1 mes',
      '1-2-meses': '1-2 meses',
      '3-6-meses': '3-6 meses',
      'mas-6-meses': 'Más de 6 meses',
      'flexible': 'Flexible'
    };
    return timelines[formData.timeline] || 'No especificado';
  };

  const handleNewBudget = () => {
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      company: '',
      projectType: '',
      budget: '',
      timeline: '',
      features: [],
      description: '',
      acceptTerms: false
    });
    setSubmitted(false);
    setErrors({});
    setSelectedService(null);
  };

  return (
    <>
      <section className={styles.budgetHero}>
        <div className={styles.heroContent}>
          <div className={styles.badge}>
            <FaCalculator className={styles.badgeIcon} />
            <span>Presupuesto Personalizado</span>
          </div>
          <h1>Solicita tu <span>Presupuesto</span></h1>
          <p>
            Cuéntanos sobre tu proyecto y te enviaremos un presupuesto detallado 
            adaptado a tus necesidades específicas.
          </p>
          <div className={styles.heroStats}>
            <div className={styles.statItem}>
              <FaRocket className={styles.statIcon} />
              <span>+100 Proyectos</span>
            </div>
            <div className={styles.statItem}>
              <FaStar className={styles.statIcon} />
              <span>5★ Calificación</span>
            </div>
            <div className={styles.statItem}>
              <FaHeart className={styles.statIcon} />
              <span>Clientes Felices</span>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.budgetSection}>
        <div className={styles.budgetContainer}>
          {submitted ? (
            <div className={styles.successMessage}>
              <div className={styles.ticketHeader}>
                <div className={styles.successIcon}>
                  <FaGift />
                </div>
                <h2>¡Tu Aventura Comienza Aquí!</h2>
                <div className={styles.ticketNumber}>
                  № {ticketNumber}
                </div>
              </div>
              
              <div className={styles.ticketBody}>
                <div className={styles.clientInfo}>
                  <h3>
                    Hola {formData.fullName.split(' ')[0]}! 
                    <FaUser className={styles.greetingIcon} />
                  </h3>
                  <p className={styles.personalMessage}>
                    {formData.company ? 
                      `Estamos emocionados de trabajar con ${formData.company} en este increíble proyecto de ${getServiceTitle().toLowerCase()}.` :
                      `Estamos emocionados de trabajar contigo en este increíble proyecto de ${getServiceTitle().toLowerCase()}.`
                    }
                  </p>
                </div>

                <div className={styles.projectSummary}>
                  <h4>
                    <FaFileAlt className={styles.sectionIcon} /> 
                    Resumen de tu Solicitud
                  </h4>
                  <div className={styles.summaryGrid}>
                    <div className={styles.summaryItem}>
                      <FaLaptopCode className={styles.itemIcon} />
                      <div className={styles.summaryContent}>
                        <span className={styles.summaryLabel}>Servicio</span>
                        <span className={styles.summaryValue}>{getServiceTitle()}</span>
                      </div>
                    </div>
                    {formData.company && (
                      <div className={styles.summaryItem}>
                        <FaBuilding className={styles.itemIcon} />
                        <div className={styles.summaryContent}>
                          <span className={styles.summaryLabel}>Empresa</span>
                          <span className={styles.summaryValue}>{formData.company}</span>
                        </div>
                      </div>
                    )}
                    <div className={styles.summaryItem}>
                      <FaEnvelope className={styles.itemIcon} />
                      <div className={styles.summaryContent}>
                        <span className={styles.summaryLabel}>Contacto</span>
                        <span className={styles.summaryValue}>{formData.email}</span>
                      </div>
                    </div>
                    {formData.timeline && (
                      <div className={styles.summaryItem}>
                        <FaCalendarAlt className={styles.itemIcon} />
                        <div className={styles.summaryContent}>
                          <span className={styles.summaryLabel}>Plazo</span>
                          <span className={styles.summaryValue}>{getTimelineText()}</span>
                        </div>
                      </div>
                    )}
                    {formData.features.length > 0 && (
                      <div className={styles.summaryItem}>
                        <FaStar className={styles.itemIcon} />
                        <div className={styles.summaryContent}>
                          <span className={styles.summaryLabel}>Características</span>
                          <span className={styles.summaryValue}>{formData.features.length} seleccionadas</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.nextSteps}>
                  <h4>
                    <FaRocket className={styles.sectionIcon} /> 
                    Próximos Pasos
                  </h4>
                  <div className={styles.stepsList}>
                    <div className={styles.step}>
                      <div className={styles.stepNumber}>1</div>
                      <div className={styles.stepContent}>
                        <strong>Análisis Detallado</strong>
                        <p>Revisaremos tu propuesta en las próximas 2-4 horas</p>
                      </div>
                    </div>
                    <div className={styles.step}>
                      <div className={styles.stepNumber}>2</div>
                      <div className={styles.stepContent}>
                        <strong>Propuesta Personalizada</strong>
                        <p>Prepararemos un plan y presupuesto a tu medida</p>
                      </div>
                    </div>
                    <div className={styles.step}>
                      <div className={styles.stepNumber}>3</div>
                      <div className={styles.stepContent}>
                        <strong>Contacto Directo</strong>
                        <p>Te contactaremos en las próximas 24-48 horas</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.motivationalQuote}>
                  <FaStar className={styles.quoteIcon} />
                  <p>"La creatividad es la inteligencia divirtiéndose"</p>
                  <span>- Albert Einstein</span>
                </div>

                <div className={styles.ticketActions}>
                  <button 
                    className={styles.newBudgetButton}
                    onClick={handleNewBudget}
                  >
                    <FaRocket /> Iniciar Nuevo Proyecto
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className={styles.formIntro}>
                <h2>Formulario de Solicitud</h2>
                <p>
                  Completa el siguiente formulario para recibir un presupuesto personalizado.
                  Cuanta más información nos proporciones, más preciso será nuestro presupuesto.
                </p>
                <div className={styles.progressIndicator}>
                  <div className={`${styles.progressStep} ${!selectedService ? styles.active : styles.completed}`}>
                    <span>1</span>
                    <label>Servicio</label>
                  </div>
                  <div className={`${styles.progressStep} ${selectedService ? styles.active : ''}`}>
                    <span>2</span>
                    <label>Detalles</label>
                  </div>
                  <div className={styles.progressStep}>
                    <span>3</span>
                    <label>Confirmación</label>
                  </div>
                </div>
              </div>

              {!selectedService ? (
                <div className={styles.serviceSelection}>
                  <h3>¿Qué servicio necesitas?</h3>
                  <div className={styles.servicesGrid}>
                    {services.map(service => (
                      <div 
                        key={service.id} 
                        className={styles.serviceCard}
                        onClick={() => handleServiceSelect(service.id)}
                      >
                        <div className={styles.serviceIcon}>
                          {service.icon}
                        </div>
                        <h4>{service.title}</h4>
                        <p>{service.description}</p>
                        <div className={styles.serviceFeatures}>
                          {service.features.slice(0, 2).map((feature, index) => (
                            <span key={index} className={styles.featureTag}>{feature}</span>
                          ))}
                        </div>
                        <button className={styles.selectButton}>
                          Seleccionar <FaArrowRight />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <form className={styles.budgetForm} onSubmit={handleSubmit}>
                  <div className={styles.selectedService}>
                    <h3>Servicio seleccionado: {services.find(s => s.id === selectedService).title}</h3>
                    <button 
                      type="button" 
                      className={styles.changeButton}
                      onClick={() => setSelectedService(null)}
                    >
                      Cambiar servicio
                    </button>
                  </div>

                  <div className={styles.formSection}>
                    <h3>Información de Contacto</h3>
                    
                    <div className={styles.formGroup}>
                      <label htmlFor="fullName">
                        Nombre Completo *
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          className={errors.fullName ? styles.inputError : ''}
                        />
                        <FaUser className={styles.inputIcon} />
                      </div>
                      {errors.fullName && <span className={styles.errorText}>{errors.fullName}</span>}
                    </div>


                      <div className={styles.formGroup}>
                        <label htmlFor="email">
                          Correo Electrónico *
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={errors.email ? styles.inputError : ''}
                          />
                          <FaEnvelope className={styles.inputIcon} />
                        </div>
                        {errors.email && <span className={styles.errorText}>{errors.email}</span>}
                      </div>

                      <div className={styles.formGroup}>
                        <label htmlFor="phone">
                          Teléfono *
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className={errors.phone ? styles.inputError : ''}
                          />
                          <FaPhone className={styles.inputIcon} />
                        </div>
                        {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                      </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="company">
                        Empresa/Organización
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                        />
                        <FaBuilding className={styles.inputIcon} />
                      </div>
                    </div>
                  </div>

                  <div className={styles.formSection}>
                    <h3>Detalles del Proyecto</h3>
                    
                        <div className={styles.formGroup}>
                          <label htmlFor="timeline">Plazo de Entrega Deseado</label>
                          <div style={{ position: 'relative' }}>
                            <select
                              id="timeline"
                              name="timeline"
                              value={formData.timeline}
                              onChange={handleChange}
                            >
                              <option value="">Seleccionar...</option>
                              <option value="urgente">Urgente (menos de 1 mes)</option>
                              <option value="1-2-meses">1-2 meses</option>
                              <option value="3-6-meses">3-6 meses</option>
                              <option value="mas-6-meses">Más de 6 meses</option>
                              <option value="flexible">Flexible</option>
                            </select>
                            <FaCalendarAlt className={styles.inputIcon} />
                          </div>
                        </div>

                    <div className={styles.formGroup}>
                      <label>Características que necesitas</label>
                      <div className={styles.featuresGrid}>
                        {services.find(s => s.id === selectedService).features.map((feature, index) => (
                          <div key={index} className={styles.featureCheckbox}>
                            <input
                              type="checkbox"
                              id={`feature-${feature}`}
                              name={`feature-${feature}`}
                              onChange={handleChange}
                              checked={formData.features.includes(feature)}
                            />
                            <label htmlFor={`feature-${feature}`}>{feature}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="description">
                        Descripción del Proyecto *
                      </label>
                      <div style={{ position: 'relative' }}>
                        <textarea
                          id="description"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          placeholder="Cuéntanos más sobre tu proyecto, objetivos y requisitos específicos..."
                          className={errors.description ? styles.inputError : ''}
                        ></textarea>
                        <FaFileAlt className={styles.inputIcon} style={{ top: '1.5rem' }} />
                      </div>
                      {errors.description && <span className={styles.errorText}>{errors.description}</span>}
                      <span className={styles.charCount}>{formData.description.length} / 1000</span>
                    </div>
                  </div>

                  <div className={styles.termsGroup}>
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={handleChange}
                      className={errors.acceptTerms ? styles.inputError : ''}
                    />
                    <label htmlFor="acceptTerms">
                      Acepto la <a href="/terminos">Política de Privacidad</a> y doy mi consentimiento para que LUCQA procese mis datos con el fin de gestionar mi solicitud.
                    </label>
                  </div>
                  {errors.acceptTerms && <span className={styles.errorText}>{errors.acceptTerms}</span>}

                  <div className={styles.formActions}>
                    <button type="submit" className={styles.submitButton}>
                      Solicitar Presupuesto
                    </button>
                  </div>

                  <div className={styles.formDisclaimer}>
                    <FaCalculator />
                    <p>
                      Este formulario es solo para solicitar un presupuesto. No implica ningún compromiso de contratación.
                      Te enviaremos una propuesta detallada que podrás revisar antes de tomar cualquier decisión.
                    </p>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default Budget;